import { useLang } from '../contexts/LanguageContext';
import { useScrollReveal, revealClass } from '../hooks/useScrollReveal';
import { CheckCircle } from 'lucide-react';

const highlights = [
  { pt: 'Trabalho pontual e organizado', es: 'Trabajo puntual y organizado' },
  { pt: 'Atenção ao detalhe em cada projeto', es: 'Atención al detalle en cada proyecto' },
  { pt: 'Materiais de qualidade garantida', es: 'Materiales de calidad garantizada' },
  { pt: 'Limpeza e respeito pelo espaço do cliente', es: 'Limpieza y respeto por el espacio del cliente' },
];

export default function About() {
  const { t, lang } = useLang();
  const { ref: leftRef, isVisible: leftVisible } = useScrollReveal();
  const { ref: rightRef, isVisible: rightVisible } = useScrollReveal();

  const stats = [
    { val: t('about_stat_1'), label: t('about_stat_1_label') },
    { val: t('about_stat_2'), label: t('about_stat_2_label') },
    { val: t('about_stat_3'), label: t('about_stat_3_label') },
    { val: t('about_stat_4'), label: t('about_stat_4_label') },
  ];

  return (
    <section id="sobre" className="py-24 sm:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div ref={leftRef} className={revealClass(leftVisible)}>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#C2410C] mb-4">
              <span className="w-6 h-px bg-[#C2410C]" />
              {t('about_label')}
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 tracking-tight mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="about-title"
            >
              {t('about_title')}
            </h2>
            <p className="text-base text-zinc-500 leading-relaxed mb-4">{t('about_p1')}</p>
            <p className="text-base text-zinc-500 leading-relaxed mb-8">{t('about_p2')}</p>

            <ul className="space-y-3 mb-10">
              {highlights.map((h, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle className="text-[#C2410C] flex-shrink-0" size={18} />
                  <span className="text-sm font-medium text-zinc-700">{lang === 'pt' ? h.pt : h.es}</span>
                </li>
              ))}
            </ul>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s, i) => (
                <div key={i} className="bg-[#FAFAFA] rounded-xl p-5 border border-zinc-100" data-testid={`about-stat-${i}`}>
                  <div
                    className="text-3xl font-bold text-zinc-900 mb-1"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {s.val}
                  </div>
                  <div className="text-xs text-zinc-500 font-medium">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right image */}
          <div ref={rightRef} className={`relative ${revealClass(rightVisible)}`} style={{ transitionDelay: '150ms' }}>
            <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-[#C2410C]/5 rounded-full" />
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#C2410C]/10 rounded-full" />
            <div className="relative overflow-hidden rounded-2xl shadow-2xl aspect-[4/5]">
              <img
                src="https://images.pexels.com/photos/30580640/pexels-photo-30580640.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Profissional em obra"
                className="w-full h-full object-cover"
                data-testid="about-image"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-zinc-900/20 via-transparent to-transparent" />
            </div>
            {/* Overlay card */}
            <div className="absolute -bottom-6 -right-4 bg-white rounded-xl shadow-xl p-5 border border-zinc-100 max-w-[200px]">
              <div className="text-2xl font-bold text-zinc-900 mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>+200</div>
              <div className="text-xs text-zinc-500 font-medium">{lang === 'pt' ? 'Clientes satisfeitos em Lisboa e Setúbal' : 'Clientes satisfechos en Lisboa y Setúbal'}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
